import {
  SavingGoalDTO,
  AdaptedSavingGoalDTO,
  GoalTransferDTO,
  AdaptedGoalTransferDTO,
  CashbackInfoDTO,
  AdaptedCashbackInfoDTO,
  GetCryptoPriceQuotesAllDTO,
  AdaptedOnRampCryptoPriceQuotesDTO,
  CryptoPriceQuotes,
  OnRampCryptoPriceQuotesDTO,
} from './transferTypes'
import { currencyUtils } from '@/utils/currency'

/**
 * Adapter for goal transfers (transactions within a saving goal)
 */
export const adapterGoalTransfer = (
  transfer: GoalTransferDTO,
): AdaptedGoalTransferDTO => {
  try {
    const getAmountMinusFee = () => {
      const amount = Number(transfer.amount.amount || 0)
      const fee = Number(transfer.fee_amount?.amount || 0)
      return amount - fee
    }

    const getFiatAmountMinusFee = () => {
      const fiatAmount = Number(transfer.amount.fiat_amount || 0)
      const fiatFee = Number(transfer.fee_amount?.fiat_amount || 0)
      return fiatAmount - fiatFee
    }

    const formattedCryptoAmount = currencyUtils.formatDynamicCurrency({
      amount: Number(transfer.amount.amount || 0),
      currencyCode: transfer.amount.currency,
      showDecimals: true, // Show decimal places as in RN
    })

    const formattedCryptoAmountMinusFee = currencyUtils.formatDynamicCurrency({
      amount: getAmountMinusFee(),
      currencyCode: transfer.amount.currency,
    })

    const formattedFiatAmountMinusFee = currencyUtils.formatFiatCurrency({
      value: getFiatAmountMinusFee(),
      fiatCode: transfer.amount.fiat_currency || 'USD',
      showDecimals: true,
    })

    const formattedFiatAmount = currencyUtils.formatFiatCurrency({
      value: Number(transfer.amount.fiat_amount || 0),
      fiatCode: transfer.amount.fiat_currency || 'USD',
      showDecimals: true,
    })

    const formattedFeeCryptoAmount = currencyUtils.formatDynamicCurrency({
      amount: Number(transfer?.fee_amount?.amount || 0),
      currencyCode: transfer?.fee_amount?.currency || 'USDT',
    })

    const formattedFeeFiatAmount = currencyUtils.formatFiatCurrency({
      value: Number(transfer?.fee_amount?.fiat_amount || 0),
      fiatCode: transfer?.fee_amount?.fiat_currency || 'USD',
    })

    // Format time for display (like in React Native)
    const formattedTime = new Date(
      transfer.created_at * 1000,
    ).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })

    // Display properties based on transfer type (like in React Native)
    const getDisplayProperties = () => {
      switch (transfer.type) {
        case 'transfer_in':
          return {
            displayTitle: 'goal.savingsDeposit',
            displayIconName: 'arrowDownCircle',
            displayIconBgColor: '#4B6CFF',
            displayIconSize: 24,
          }
        case 'transfer_out':
          return {
            displayTitle: 'goal.savingsWithdrawal',
            displayIconName: 'arrowRightCircle',
            displayIconBgColor: '#000',
            displayIconSize: 24,
          }
        case 'earning':
          return {
            displayTitle: 'goal.dailyEarnings',
            displayIconName: 'dollar', // Use dollar icon as on /activity page
            displayIconBgColor: '#34C759', // Green color for earnings
            displayIconSize: 16, // Reduced size for dollar icon
          }
        default:
          return {
            displayTitle: 'activityNotMapped',
          }
      }
    }

    const result = {
      ...transfer,
      created_at: new Date(transfer.created_at * 1000), // Convert from timestamp
      formattedDate: new Date(transfer.created_at * 1000).toLocaleDateString(
        'en-US',
        {
          day: '2-digit',
          month: 'short',
        },
      ),
      formattedTime,
      formattedDateWithTime: `${new Date(transfer.created_at * 1000).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })} ${formattedTime}`,
      formattedCryptoAmount,
      formattedFiatAmount,
      formattedCryptoAmountMinusFee,
      formattedFiatAmountMinusFee,
      formattedFeeCryptoAmount,
      formattedFeeFiatAmount,
      ...getDisplayProperties(),
    }

    return result
  } catch (error) {
    console.error('Error in adapterGoalTransfer:', error)
    throw error
  }
}

/**
 * Adapter for saving goals - transforms raw API data to formatted UI data
 */
export const adapterSavingGoal = (
  goal: SavingGoalDTO,
): AdaptedSavingGoalDTO => {
  const remainingToGoal =
    Number(goal.goal_amount || 0) - Number(goal.staked_amount.amount || 0)

  const goalAmountFormatted = currencyUtils.formatFiatCurrency({
    value: Number(goal.goal_amount || 0),
    fiatCode: 'USD',
  })

  const stakedFiatAmountFormatted = currencyUtils.formatFiatCurrency({
    value: Number(goal.staked_amount.fiat_amount || 0),
    fiatCode: goal.staked_amount.fiat_currency || 'USD',
    showDecimals: true,
  })

  const stakedCryptoAmountFormatted = currencyUtils.formatDynamicCurrency({
    amount: Number(goal.staked_amount.amount || 0),
    currencyCode: goal.staked_amount.currency || 'USDT',
  })

  const stakedMaskedStableCoinAsFiat = currencyUtils.formatDynamicCurrency({
    amount: Number(goal.staked_amount.amount || 0),
    currencyCode: goal.staked_amount.currency || 'USDT',
    shouldMaskStablecoinAsFiat: true,
  })

  const currentEarningsFormatted = currencyUtils.formatFiatCurrency({
    value: Number(goal.current_earnings),
    fiatCode: 'USD',
    showDecimals: true,
  })

  const remainingToGoalFormatted = currencyUtils.formatFiatCurrency({
    value: remainingToGoal,
    fiatCode: 'USD',
  })

  return {
    ...goal,
    transfers: goal.transfers?.map(adapterGoalTransfer) || [],
    goalAmountFormatted,
    stakedFiatAmountFormatted,
    stakedCryptoAmountFormatted,
    stakedMaskedStableCoinAsFiat,
    currentEarningsFormatted,
    remainingToGoalFormatted,
  }
}

/**
 * Adapter for cashback info - transforms raw API data to formatted UI data
 * Matches React Native adapter structure for consistency
 */
export const adapterCashbackInfo = (
  cashbackInfo: CashbackInfoDTO,
): AdaptedCashbackInfoDTO => {
  // Extract values from nested cashback_balance object
  const balance = cashbackInfo.cashback_balance.amount
  const currency = cashbackInfo.cashback_balance.currency
  const fiatAmount = cashbackInfo.cashback_balance.fiat_amount
  const fiatCurrency = cashbackInfo.cashback_balance.fiat_currency

  // Format crypto amount with proper decimals
  const formattedCryptoAmount = currencyUtils.formatDynamicCurrency({
    amount: Number(balance || 0),
    currencyCode: currency,
    showDecimals: true,
  })

  // Format fiat amount with proper currency symbol
  const formattedFiatAmount = currencyUtils.formatFiatCurrency({
    value: Number(fiatAmount || 0),
    fiatCode: fiatCurrency || 'USD',
    showDecimals: true,
  })

  // Format lifetime spent with proper currency
  const formattedLifetimeSpent = currencyUtils.formatDynamicCurrency({
    amount: Number(cashbackInfo.lifetime_spent || 0),
    currencyCode: cashbackInfo.lifetime_spent_currency as any,
    showDecimals: true,
  })

  // Format next threshold with proper currency
  const formattedNextThreshold = currencyUtils.formatDynamicCurrency({
    amount: Number(cashbackInfo.next_threshold || 0),
    currencyCode: cashbackInfo.lifetime_spent_currency as any,
    showDecimals: true,
  })

  // Format total paid cashback (all-time) - matches RN adapter
  // Use raw amount with BTC text instead of ₿ symbol
  const totalPaidAmount = Number(cashbackInfo.total_payed_cashback?.amount || 0)
  const totalPaidCurrency = cashbackInfo.total_payed_cashback?.currency || 'BTC'
  const formattedTotalPaidCashback = `${totalPaidAmount.toFixed(8).replace(/\.?0+$/, '')} ${totalPaidCurrency}`

  const formattedTotalPaidCashbackFiat = currencyUtils.formatFiatCurrency({
    value: Number(cashbackInfo.total_payed_cashback?.fiat_amount || 0),
    fiatCode: cashbackInfo.total_payed_cashback?.fiat_currency || 'USD',
    showDecimals: true,
  })

  // Format weekly amounts (from cashback_balance) - matches RN adapter naming
  // Use raw amount with BTC text instead of ₿ symbol
  const weeklyAmount = Number(cashbackInfo.cashback_balance.amount || 0)
  const weeklyCurrency = cashbackInfo.cashback_balance.currency || 'BTC'
  const formattedWeeklyCryptoAmount = `${weeklyAmount.toFixed(8).replace(/\.?0+$/, '')} ${weeklyCurrency}`

  const formattedWeeklyFiatAmount = currencyUtils.formatFiatCurrency({
    value: Number(cashbackInfo.cashback_balance.fiat_amount || 0),
    fiatCode: cashbackInfo.cashback_balance.fiat_currency || 'USD',
    showDecimals: true,
  })

  // Parse numeric values for convenience
  const percentChange24h = Number(
    cashbackInfo.cashback_balance.percent_change_24h || 0,
  )
  const pricePerUnit = Number(cashbackInfo.cashback_balance.price_for_unit || 0)
  const revenue24hFiatAmount = Number(
    cashbackInfo.cashback_balance.revenue_24h_fiat_amount || 0,
  )

  const result: AdaptedCashbackInfoDTO = {
    ...cashbackInfo,
    // Backward compatibility: flatten nested structure
    balance,
    currency,
    // Formatted display fields
    formattedCryptoAmount,
    formattedFiatAmount,
    formattedLifetimeSpent,
    formattedNextThreshold,
    formattedTotalPaidCashback,
    formattedTotalPaidCashbackFiat,
    formattedWeeklyCryptoAmount,
    formattedWeeklyFiatAmount,
    // Computed numeric fields
    percentChange24h,
    pricePerUnit,
    revenue24hFiatAmount,
  }

  return result
}

/**
 * Adapter for on-ramp crypto price quotes fees
 */
export const adapterCryptoPriceQuotesFees = ({
  onRampCryptoPriceQuotes,
  adapterParams,
  payload,
}: {
  onRampCryptoPriceQuotes: GetCryptoPriceQuotesAllDTO
  adapterParams: { paymentMethod: string }
  payload: OnRampCryptoPriceQuotesDTO
}): AdaptedOnRampCryptoPriceQuotesDTO => {
  try {
    // Find price quote for the specific payment method
    const priceQuotesByPaymentMethod =
      onRampCryptoPriceQuotes.price_quotes.find(
        (quote) => quote.payment_method === adapterParams.paymentMethod,
      )

    if (!priceQuotesByPaymentMethod) {
      return {
        ...onRampCryptoPriceQuotes,
        formattedFiatFee: '0.00 USD',
        formattedAmountWithPaymentCurrencyRelative: '0.00 USD',
        formattedFiatTotal: '0.00 USD',
        priceQuotesByPaymentMethod: undefined,
      }
    }

    // Format fee amount
    const formattedFiatFee = currencyUtils.formatFiatCurrency({
      value: Number(priceQuotesByPaymentMethod.estimated_fiat_fee.amount || 0),
      fiatCode: priceQuotesByPaymentMethod.estimated_fiat_fee.currency as any,
      showDecimals: true,
    })

    // Format amount with payment currency
    const formattedAmountWithPaymentCurrencyRelative =
      currencyUtils.formatFiatCurrency({
        value: Number(priceQuotesByPaymentMethod.fiat_amount.amount || 0),
        fiatCode: priceQuotesByPaymentMethod.fiat_amount.currency as any,
        showDecimals: true,
      })

    // Calculate and format total (amount + fee)
    const totalAmount =
      Number(priceQuotesByPaymentMethod.fiat_amount.amount || 0) +
      Number(priceQuotesByPaymentMethod.estimated_fiat_fee.amount || 0)

    const formattedFiatTotal = currencyUtils.formatFiatCurrency({
      value: totalAmount,
      fiatCode: priceQuotesByPaymentMethod.fiat_amount.currency as any,
      showDecimals: true,
    })

    return {
      ...onRampCryptoPriceQuotes,
      formattedFiatFee,
      formattedAmountWithPaymentCurrencyRelative,
      formattedFiatTotal,
      priceQuotesByPaymentMethod,
    }
  } catch (error) {
    console.error('Error in adapterCryptoPriceQuotesFees:', error)
    return {
      ...onRampCryptoPriceQuotes,
      formattedFiatFee: '0.00 USD',
      formattedAmountWithPaymentCurrencyRelative: '0.00 USD',
      formattedFiatTotal: '0.00 USD',
      priceQuotesByPaymentMethod: undefined,
    }
  }
}

export const transferAdapters = {
  adapterGoalTransfer,
  adapterSavingGoal,
  adapterCashbackInfo,
  adapterCryptoPriceQuotesFees,
}
